import { just } from 'maybeasy';
import { findLinkBy } from '../../../../../../Links';
import Description from './Description';
import { HtmlView } from '../ExperienceHtmlView';
import PreviewCollectionLogo from '../../../Preview/AdminPreview/PreviewCollectionLogo';
import Title from './Title';
import OrganizationLogo from './OrganizationLogo';
import { useReadyBuilder } from './useReadyBuilder';
import { useLearningCollection } from '../LearningCollectionContext';

// DVLP-7601: preview mode swaps the inline Title/Description editors and the
// LogoUploader for static text + a plain image, so the admin can see what a
// participant would see without any edit chrome. `stateKind` is threaded from
// ReadyBuilder so this component derives its own read-only state rather than
// accepting an optional boolean flag.
//
// The read-only header follows the participant page (platform3
// ExperienceSelectionView/WelcomeMessage after DVLP-7805): the description
// goes through the same HtmlView, so paragraphs, lists, links and the legacy
// embedded image lay out the way a participant sees them (a raw innerHTML
// dump collapsed every paragraph onto the next line); the text takes the
// full width unless an uploaded logo is actually showing beside it.

interface Props {
  stateKind: 'edit' | 'new' | 'preview';
}

function Details({ stateKind }: Props) {
  const editable = stateKind !== 'preview';
  const {
    resourceState,
    handleLearningCollectionNameChange,
    handleLearningCollectionDescriptionChange,
  } = useReadyBuilder();
  const { setResourceState } = useLearningCollection();
  const { name, description } = resourceState.payload;

  const logoLink = findLinkBy({ rel: 'learning-collection-logo' }, resourceState.links);
  const uploadLink = findLinkBy({ rel: 'direct-upload' }, resourceState.links);

  if (!editable) {
    return (
      <div className="flex flex-col justify-between pb-10 md:flex-row md:gap-20">
        <div className="w-full justify-center">
          <h1
            style={{
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-weight-semibold)',
              lineHeight: 1.2,
            }}
          >
            {name}
          </h1>
          <HtmlView
            description={description}
            className="mt-4 text-muted-foreground [&_p]:text-muted-foreground"
          />
        </div>
        <PreviewCollectionLogo link={logoLink} />
      </div>
    );
  }

  return (
    <>
      <div className="flex pb-10 justify-between">
        <div className="md:mr-11 w-3/5">
          <Title
            name={name}
            handleLearningCollectionNameChange={handleLearningCollectionNameChange}
          />
          <div className="mt-4">
            <Description
              description={description}
              onDescriptionChange={handleLearningCollectionDescriptionChange}
              emptyStateText={just('Add a collection description')}
              placeholder={just('Enter description...')}
              intercomTarget={just('builder-v2-collection-description-field')}
            />
          </div>
        </div>
        <OrganizationLogo
          logoLink={logoLink}
          uploadLink={uploadLink}
          links={resourceState.links}
          resourceState={resourceState}
          setResourceState={setResourceState}
        />
      </div>
    </>
  );
}

export default Details;
